import { Link, useSearchParams } from 'react-router-dom';
import ClientLayout from '../../layouts/ClientLayout';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ordersAPI } from '../../api/endpoints';

const CheckoutSuccess = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderId');

    const {
        data: orderResponse,
        isLoading: isLoadingOrder,
        error: orderError,
    } = useQuery({
        queryKey: ['order', orderId],
        queryFn: async () => {
            const res = await ordersAPI.getOrder(orderId);
            return res?.data;
        },
        enabled: Boolean(orderId),
        refetchOnMount: true,
        retry: 1,
    });

    // L’API peut renvoyer directement l’objet commande (ex: { id, items, ... })
    // ou un wrapper { data: { ... } }. On supporte les deux.
    const order = orderResponse?.data ?? orderResponse ?? null;
    const orderItems = Array.isArray(order?.items) ? order.items : [];

    const isMultiVendor = (() => {
        // Cas 1: l'API a déjà split en commandes fournisseurs
        if (Array.isArray(order?.individualOrders) && order.individualOrders.length > 1) {
            return true;
        }

        // Cas 2: on déduit depuis les items (shop/vendor uniques)
        const vendorKeys = new Set();
        for (const it of orderItems) {
            const key =
                it?.vendorId ??
                it?.vendor?._id ??
                it?.vendor?.id ??
                it?.shopId ??
                it?.shop?._id ??
                it?.shop?.id ??
                it?.product?.shopId ??
                it?.product?.shop?._id ??
                it?.product?.shop?.id ??
                it?.product?.vendorId ??
                it?.product?.vendor?._id ??
                it?.product?.vendor?.id ??
                it?.shopName ??
                it?.product?.shop?.shop_name ??
                it?.product?.shop?.name ??
                null;

            if (key != null && String(key).trim() !== '') {
                vendorKeys.add(String(key));
            }
        }
        return vendorKeys.size > 1;
    })();

    const orderNumber =
        order?.numeroCommande ??
        order?.id ??
        orderId ??
        '—';

    const orderDateRaw =
        order?.createdAt ?? null;
    const orderDate = orderDateRaw
        ? new Date(orderDateRaw).toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
        : '—';

    const shipping = Number(order?.fraisLivraison ?? 0);
    const reduction = Number(order?.reduction ?? 0);
    const subtotal = orderItems.reduce(
        (sum, it) => sum + Number(it?.totalPrice ?? 0),
        0
    );
    const total = Number(order?.prixTotal ?? Math.max(0, subtotal - reduction + shipping));

    const paymentMethodLabel = (() => {
        const method = (
            order?.paymentMethod ??
            order?.payment_method ??
            ''
        )
            .toString()
            .trim()
            .toLowerCase();

        if (
            method === 'online_kkiapay' ||
            method === 'online_fedapay' ||
            method === 'online'
        ) {
            return t('checkout.totals.online');
        }
        if (method === 'pickup_warehouse') {
            return t('checkout.totals.pickup_warehouse', {
                defaultValue: 'Retrait entrepôt',
            });
        }
        if (method === 'cash_on_delivery' || !method) {
            return t('checkout_success.order_details.payment_on_delivery');
        }
        return method;
    })();

    const paymentStatus = (
        order?.paymentStatus ??
        order?.payment_status ??
        ''
    )
        .toString()
        .toLowerCase();

    const paymentStatusLabel =
        paymentStatus === 'paid'
            ? t('checkout_success.payment_status.paid', { defaultValue: 'Payé' })
            : paymentStatus === 'failed'
              ? t('checkout_success.payment_status.failed', {
                    defaultValue: 'Paiement échoué',
                })
              : paymentStatus === 'refunded'
                ? t('checkout_success.payment_status.refunded', {
                      defaultValue: 'Remboursé',
                  })
                : t('checkout_success.payment_status.unpaid', {
                      defaultValue: 'Non payé',
                  });

    const user = order?.user ?? null;
    const deliveryAddress = order?.adresseLivraison ?? null;

    // eslint-disable-next-line no-unused-vars
    const deliveryLines = [
        deliveryAddress?.name ?? '',
        deliveryAddress?.address ?? '',
    ].filter(Boolean);

    const customerLines = [
        user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '',
        user?.email ?? '',
        user?.phone ?? user?.other_phone ?? '',
        user?.address ?? '',
    ].filter(Boolean);

    const statutLabel = order?.statut ?? '—';

    return (
        <ClientLayout>
            <div className="flex-1 px-4 sm:px-6 md:px-0 md:mx-8 lg:mx-16 mx-auto py-8">
                    {/* Breadcrumbs */}
                    <div className="mb-4">
                        <nav className="text-sm text-gray-600">
                            <Link to="/" className="hover:text-gray-900">{t('checkout_success.breadcrumb.home')}</Link>
                            <span className="mx-2">&gt;</span>
                            <Link to="/checkout" className="hover:text-gray-900">{t('checkout_success.breadcrumb.checkout')}</Link>
                            <span className="mx-2">&gt;</span>
                            <span className="text-gray-900">{t('checkout_success.breadcrumb.order_received')}</span>
                        </nav>
                    </div>

                    {/* Order Confirmation Banner */}
                    <div className="mb-4">
                        <div className="bg-green-50 border-2 border-dashed border-green-300 rounded-lg p-4 text-center">
                            <p className="text-green-800 text-base font-medium">
                                {t('checkout_success.confirmation.message')}
                            </p>
                        </div>
                    </div>

                    {/* Order Summary Card */}
                    <div className="bg-gray-100 rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {t('checkout_success.order_summary.order_number')}
                                </label>
                                <p className="text-base font-semibold text-gray-900">{orderNumber}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {t('checkout_success.order_summary.payment_method')}
                                </label>
                                <p className="text-base font-semibold text-gray-900">{paymentMethodLabel}</p>
                                <p className="text-sm text-gray-500 mt-0.5">{paymentStatusLabel}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {t('checkout_success.order_summary.date')}
                                </label>
                                <p className="text-base font-semibold text-gray-900">{orderDate}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">
                                    {t('checkout_success.order_summary.note')}
                                </label>
                                <p className="text-base font-semibold text-gray-900">{statutLabel}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Content - 2/3 width */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Order Details Section */}
                            <div className="mt-4">
                                <h2 className="text-base font-semibold text-gray-900 mb-2">{t('checkout_success.order_details.title')}</h2>

                                {!orderId ? (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                                        {t('checkout_success.errors.missing_order_id')}
                                    </div>
                                ) : isLoadingOrder ? (
                                    <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6 text-sm text-gray-600">
                                        {t('checkout.totals.loading')}
                                    </div>
                                ) : orderError ? (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                                        {t('checkout.totals.error')}
                                    </div>
                                ) : (
                                <div className="overflow-x-auto bg-gray-50 rounded-lg shadow-sm border border-gray-200">
                                    <table className='w-full'>
                                        <thead>
                                            <tr className='border'>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.order_details.product')}</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border w-1/4">{t('checkout_success.order_details.detail')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderItems.length === 0 ? (
                                                <tr className="border">
                                                    <td className="px-4 py-3 text-sm text-gray-600 border" colSpan={2}>
                                                        {t('checkout.totals.empty')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                orderItems.map((it, idx) => {
                                                    const name = it?.productName ?? it?.product?.name ?? 'Produit';
                                                    const qty = Number(it?.quantity ?? 1);
                                                    const unitPrice = Number(it?.unitPrice ?? 0);
                                                    const lineTotal = Number(it?.totalPrice ?? unitPrice * qty);
                                                    return (
                                                        <tr key={it?._id ?? it?.id ?? `${idx}`} className="border">
                                                            <td className="px-4 py-3 text-sm text-gray-900 border">
                                                                {name} × {qty}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-900 border font-medium whitespace-nowrap">
                                                                {lineTotal.toLocaleString()} FCFA
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                            <tr className="border">
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium">{t('checkout_success.order_details.total')}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium whitespace-nowrap">{Number(subtotal || 0).toLocaleString()} FCFA</td>
                                            </tr>
                                            <tr className="border">
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium">{t('checkout_success.order_details.shipping')}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium whitespace-nowrap">{Number(shipping || 0).toLocaleString()} FCFA</td>
                                            </tr>
                                            <tr className="border">
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium">{t('checkout_success.order_details.discount')}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium whitespace-nowrap">- {Number(reduction || 0).toLocaleString()} FCFA</td>
                                            </tr>
                                            <tr className="border">
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium">{t('checkout_success.order_details.payment_method_label')}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium">
                                                    {paymentMethodLabel}
                                                </td>
                                            </tr>
                                            <tr className="border">
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium">{t('checkout_success.order_details.total')}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 border font-medium whitespace-nowrap">{Number(total || 0).toLocaleString()} FCFA</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                )}
                            </div>

                            {/* Individual Orders Table Section */}
                            {/* {Array.isArray(order?.individualOrders) && order.individualOrders.length > 0 ? (
                                <div className="">
                                    <div className="overflow-x-auto bg-gray-50 rounded-lg shadow-sm border border-gray-200">
                                        <table className="w-full">
                                            <thead className="border-b border-gray-200">
                                                <tr className='border'>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.order')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.date')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.status')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.total')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {order.individualOrders.map((child, index) => (
                                                    <tr key={child?.id ?? child?._id ?? index} className="hover:bg-gray-50 border font-medium">
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.orderNumber ?? child?.numeroCommande ?? child?.id ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.date ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.status ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.total ?? '—'}</td>
                                                        <td className="px-4 py-3 border">
                                                            <button className="rounded-full px-3 py-1 text-xs bg-gray-200 text-gray-800 font-medium hover:bg-gray-300">
                                                                {t('checkout_success.individual_orders.view')}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : null} */}

                            {/* Individual Orders Table Section */}
                            {/* {Array.isArray(order?.individualOrders) && order.individualOrders.length > 0 ? (
                                <div className="">
                                    <div className="overflow-x-auto bg-gray-50 rounded-lg shadow-sm border border-gray-200">
                                        <table className="w-full">
                                            <thead className="border-b border-gray-200">
                                                <tr className='border'>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.order')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.date')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.status')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.total')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {order.individualOrders.map((child, index) => (
                                                    <tr key={child?.id ?? child?._id ?? index} className="hover:bg-gray-50 border font-medium">
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.orderNumber ?? child?.numeroCommande ?? child?.id ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.date ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.status ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.total ?? '—'}</td>
                                                        <td className="px-4 py-3 border">
                                                            <button className="rounded-full px-3 py-1 text-xs bg-gray-200 text-gray-800 font-medium hover:bg-gray-300">
                                                                {t('checkout_success.individual_orders.view')}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : null} */}

                            {/* Individual Orders Table Section */}
                            {/* {Array.isArray(order?.individualOrders) && order.individualOrders.length > 0 ? (
                                <div className="">
                                    <div className="overflow-x-auto bg-gray-50 rounded-lg shadow-sm border border-gray-200">
                                        <table className="w-full">
                                            <thead className="border-b border-gray-200">
                                                <tr className='border'>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.order')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.date')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.status')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border">{t('checkout_success.individual_orders.total')}</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {order.individualOrders.map((child, index) => (
                                                    <tr key={child?.id ?? child?._id ?? index} className="hover:bg-gray-50 border font-medium">
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.orderNumber ?? child?.numeroCommande ?? child?.id ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.date ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.status ?? '—'}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-900 border">{child?.total ?? '—'}</td>
                                                        <td className="px-4 py-3 border">
                                                            <button className="rounded-full px-3 py-1 text-xs bg-gray-200 text-gray-800 font-medium hover:bg-gray-300">
                                                                {t('checkout_success.individual_orders.view')}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : null} */}

                            {/* Addresses Section */}
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Client */}
                                    <div>
                                        <h3 className="text-base font-semibold text-gray-900 mb-2.5">{t('checkout_success.addresses.billing')}</h3>
                                        <div className="space-y-2 text-sm text-gray-500 font-medium">
                                            {customerLines.length ? customerLines.map((line, idx) => <p key={idx}>{line}</p>) : <p>—</p>}
                                        </div>
                                    </div>

                                    {/* Adresse de livraison */}
                                    <div>
                                        <h3 className="text-base font-semibold text-gray-900 mb-2.5">{t('checkout_success.addresses.shipping')}</h3>
                                        <div className="space-y-2 text-sm text-gray-500 font-medium">
                                            <p>{deliveryAddress?.name ?? '—'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Sidebar - 1/3 width */}
                        <div className="lg:col-span-1">
                            {/* Remark Card */}
                            {isMultiVendor ? (
                                <div className="bg-[#8A250E] rounded-lg shadow-sm border border-[#8A250E] p-6 mt-12">
                                    <div className="flex items-center mb-4">
                                        <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-white font-bold text-lg">{t('checkout_success.note.title')}</h3>
                                    </div>
                                    <p className="text-white text-sm leading-relaxed">
                                        {t('checkout_success.note.message')}
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    </div>
            </div>
        </ClientLayout>
    );
};

export default CheckoutSuccess;
